import { useEffect, useLayoutEffect, useState } from 'react';
import type { CSSProperties, ReactElement, RefCallback } from 'react';

import type { PersonModel } from '@/domain/models/personModel';
import { useAppState } from '@/presentation/stores/appStateStore';
import { BackgroundWrapper } from '@/presentation/widgets/BackgroundWrapper';
import { useIsLandscape } from '@/utils/useScreen';

import { PersonCard } from './PersonCard';
import styles from './PersonsPage.module.css';

/**
 * Перенос `lib/src/presentation/pages/persons/persons_page.dart`.
 *
 * `Scaffold(extendBodyBehindAppBar: true)` отдельного узла не требует: фон
 * приложения рисуется один раз под роутером (`AppBackground`), а AppBar у
 * экрана нет — тело и так занимает весь экран.
 */

/** `const rowCount = 2` */
const ROW_COUNT = 2;

/**
 * `const spacing = 20.0`. В Dart число идёт без `.w` — значит, это
 * логические пиксели, и масштабировать его не надо. Само значение живёт
 * ещё и в CSS-модуле (`gap`), здесь оно нужно для формулы `mainAxisExtent`.
 */
const GRID_SPACING = 20;

/** `Future.delayed(Duration(seconds: 1))` — окно троттлинга ввода. */
const INPUT_THROTTLE_MS = 1000;

/** `appState.config?.persons ?? []` — пустой список одной и той же ссылкой. */
const NO_PERSONS: readonly PersonModel[] = [];

/**
 * `FutureBuilder(future: _throttleDuration)` +
 * `IgnorePointer(ignoring: snapshot.connectionState != ConnectionState.done)`.
 *
 * Первую секунду после открытия экрана нажатия не проходят. В Dart будущее
 * создаётся в поле `State`, то есть один раз на всё время жизни экрана, —
 * здесь это таймер из эффекта монтирования, перерисовки его не трогают.
 */
function useInputThrottle(): boolean {
  const [ignoring, setIgnoring] = useState(true);
  useEffect(() => {
    const timer = window.setTimeout(() => setIgnoring(false), INPUT_THROTTLE_MS);
    return () => window.clearTimeout(timer);
  }, []);
  return ignoring;
}

/** Высота контентной коробки: `clientHeight` считает поля, а они здесь есть. */
function measureContentHeight(node: HTMLElement): number {
  const style = getComputedStyle(node);
  const vertical = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
  return Number.isFinite(vertical) ? node.clientHeight - vertical : node.clientHeight;
}

interface MeasuredContent {
  readonly ref: RefCallback<HTMLDivElement>;
  /** `null` — до первого замера. */
  readonly height: number | null;
}

/**
 * Замена `LayoutBuilder` вместе с `MediaQuery.of(context).padding`.
 *
 * Измеряется контентная коробка контейнера прокрутки, а не окно: его поля —
 * это ровно `SliverSafeArea` плюс `EdgeInsets.all(40)`, поэтому замер сразу
 * равен `constraints.biggest.height - top - bottom - padding.vertical` из
 * Dart. Вырезы экрана (`env(safe-area-inset-*)`) числом из JS не достать —
 * замер снимает и их.
 *
 * Замер идёт в `useLayoutEffect`, чтобы первый кадр уже был с настоящей
 * высотой: `ResizeObserver` сообщит о размере только на следующем проходе.
 */
function useMeasuredContentHeight(): MeasuredContent {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (node === null) return;
    setHeight(measureContentHeight(node));
    const observer = new ResizeObserver(() => {
      setHeight(measureContentHeight(node));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref: setNode, height };
}

/**
 * `mainAxisExtent` из Dart слово в слово:
 *
 *     (constraints.biggest.height
 *       - (persons.length / rowCount * spacing)
 *       - padding.vertical - top - bottom) / 2
 *
 * Формула странная — вычитаемый промежуток зависит от числа аватаров, — но
 * работает именно так, и порт её не исправляет. Три последних слагаемых уже
 * вычтены замером (см. [useMeasuredContentHeight]).
 *
 * Отрицательный размер в CSS невозможен, поэтому он обрезается нулём: во
 * Flutter такой случай кончился бы падением раскладки.
 */
function mainAxisExtent(contentHeight: number, personCount: number): number {
  const spacingTotal = (personCount / ROW_COUNT) * GRID_SPACING;
  return Math.max(0, (contentHeight - spacingTotal) / 2);
}

export function PersonsPage(): ReactElement {
  const appState = useAppState();
  const persons = appState.config?.persons ?? NO_PERSONS;
  // Список намеренно не фильтруется по `isUnrealPerson`: в Dart фильтра
  // здесь тоже нет, хотя геттер существует.
  const isLandscape = useIsLandscape();
  const ignoring = useInputThrottle();
  const { ref, height } = useMeasuredContentHeight();

  const extent = height === null ? null : mainAxisExtent(height, persons.length);
  const gridStyle =
    extent === null
      ? undefined
      : ({ '--main-axis-extent': `${extent}px` } as CSSProperties);

  return (
    <div className={ignoring ? `${styles.root} ${styles.ignoring}` : styles.root}>
      <BackgroundWrapper>
        {/*
          `key: ValueKey(context.isLandscape)` — смена ориентации меняет ось
          прокрутки, и список пересоздаётся вместе с её положением.
        */}
        <div key={String(isLandscape)} ref={ref} className={styles.scroll}>
          <div className={styles.grid} style={gridStyle}>
            {persons.map((person) => (
              <PersonCard key={person.id} person={person} />
            ))}
          </div>
        </div>
      </BackgroundWrapper>
    </div>
  );
}
